import type { IProperty } from 'src/services/api/properties/types';

/**
 * One row of a property-specific AMI income limit, as stored in the
 * `ami_income_limits` property fact. Note the shape differs from the global
 * AMI limits endpoint: here `band` is a display string (e.g. "60%") and the
 * household size is called `household_size`.
 */
export interface AmiIncomeLimit {
    /** AMI band as a display string, e.g. "60%". */
    band: string;
    /** Household size this limit applies to. */
    household_size: number;
    /** Annual gross income limit in dollars. */
    amount: number;
}

const isAmiIncomeLimit = (value: unknown): value is AmiIncomeLimit => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const entry = value as Record<string, unknown>;
    return (
        typeof entry.band === 'string' &&
        typeof entry.household_size === 'number' &&
        typeof entry.amount === 'number'
    );
};

/**
 * Parse the `ami_income_limits` property fact into typed rows.
 *
 * The fact's value is stored as JSONB on the backend, so it arrives as a
 * native array (or, defensively, a JSON-encoded string) rather than the plain
 * `string` that `IPropertyFact.value` is typed as. Returns null when the fact
 * is absent, and drops any malformed entries.
 */
export const parsePropertyAmiIncomeLimits = (
    property: IProperty | null | undefined
): AmiIncomeLimit[] | null => {
    const fact = property?.facts?.ami_income_limits;
    if (!fact) {
        return null;
    }

    const rawValue: unknown = fact.value;
    let parsed: unknown = rawValue;
    if (typeof rawValue === 'string') {
        try {
            parsed = JSON.parse(rawValue);
        } catch {
            return null;
        }
    }

    if (!Array.isArray(parsed)) {
        return null;
    }

    return parsed.filter(isAmiIncomeLimit);
};
